import type { PepLocale } from "../i18n/types";

/**
 * Knowledge Center — WP-3 editorial (FR/EN).
 * Deployment modes = official `DeploymentMode` from itaas-platform-public@1.0.
 * VPN = connectivity pattern supporting private / on-prem / hybrid / partner modes (not a new DeploymentMode).
 * Product FAQs → BACKLOG V1.1 only.
 */

export const KNOWLEDGE_DEPLOYMENT_ASSET = {
  src: "/assets/knowledge/deployment-models-infrastructure.png",
  width: 1024,
  height: 1024,
  altFr:
    "Illustration isométrique représentant une infrastructure cloud sécurisée et différents modèles de déploiement IT.",
  altEn:
    "Isometric illustration of a secure cloud infrastructure and IT deployment models.",
} as const;

export type KnowledgeFaqCategory =
  | "all"
  | "fonctionnement"
  | "deploiement"
  | "securite"
  | "connectivite"
  | "donnees"
  | "licences"
  | "support"
  | "evolutivite";

export const KNOWLEDGE_FAQ_CATEGORIES: Array<{
  id: KnowledgeFaqCategory;
  labelFr: string;
  labelEn: string;
}> = [
  { id: "all", labelFr: "Tout", labelEn: "All" },
  { id: "fonctionnement", labelFr: "Fonctionnement", labelEn: "How it works" },
  { id: "deploiement", labelFr: "Déploiement", labelEn: "Deployment" },
  { id: "securite", labelFr: "Sécurité", labelEn: "Security" },
  { id: "connectivite", labelFr: "Connectivité", labelEn: "Connectivity" },
  { id: "donnees", labelFr: "Données", labelEn: "Data" },
  { id: "licences", labelFr: "Licences", labelEn: "Licensing" },
  { id: "support", labelFr: "Support", labelEn: "Support" },
  { id: "evolutivite", labelFr: "Évolutivité", labelEn: "Scalability" },
];

/** Official DeploymentMode cards (+ VPN connectivity). */
export const KNOWLEDGE_DEPLOYMENT_MODELS = [
  {
    id: "SAAS",
    mode: "SAAS" as const,
    activeV1: true,
    titleFr: "SaaS (Cloud public)",
    titleEn: "SaaS (Public cloud)",
    bodyFr:
      "La suite est opérée et mise à jour par ITaaS. Accès navigateur, démarrage rapide, maintenance et sécurité portés par la plateforme.",
    bodyEn:
      "The suite is operated and updated by ITaaS. Browser access, fast start, maintenance and security carried by the platform.",
    href: "/contact?type=demo",
  },
  {
    id: "PRIVATE_CLOUD",
    mode: "PRIVATE_CLOUD" as const,
    activeV1: false,
    titleFr: "Cloud privé",
    titleEn: "Private cloud",
    bodyFr:
      "Environnement dédié pour votre organisation, avec les mêmes modules ITaaS Suite et un contrôle accru sur l’isolation et le réseau.",
    bodyEn:
      "A dedicated environment for your organisation, with the same ITaaS Suite modules and stronger control over isolation and networking.",
    href: "/contact?type=info",
  },
  {
    id: "ON_PREMISE",
    mode: "ON_PREMISE" as const,
    activeV1: false,
    titleFr: "On-Premise",
    titleEn: "On-Premise",
    bodyFr:
      "Installation dans votre datacenter ou infrastructure. Vous conservez la maîtrise du matériel ; ITaaS accompagne le déploiement et les mises à jour.",
    bodyEn:
      "Install in your datacenter or infrastructure. You keep control of the hardware; ITaaS supports rollout and updates.",
    href: "/contact?type=info",
  },
  {
    id: "HYBRID",
    mode: "HYBRID" as const,
    activeV1: false,
    titleFr: "Hybride",
    titleEn: "Hybrid",
    bodyFr:
      "Combine SaaS et composants privés ou on-premise selon vos contraintes métier, réglementaires ou de connectivité.",
    bodyEn:
      "Combines SaaS with private or on-premise components according to your business, regulatory or connectivity constraints.",
    href: "/contact?type=info",
  },
  {
    id: "PARTNER_HOSTED",
    mode: "PARTNER_HOSTED" as const,
    activeV1: false,
    titleFr: "Hébergement partenaire (Partner Hosted)",
    titleEn: "Partner Hosted",
    bodyFr:
      "Hébergement opéré avec un partenaire agréé — utile pour des exigences locales d’hébergement tout en restant sur le socle ITaaS Suite.",
    bodyEn:
      "Hosting operated with an approved partner — useful for local hosting requirements while staying on the ITaaS Suite foundation.",
    href: "/partners",
  },
  {
    id: "VPN",
    mode: null,
    activeV1: false,
    titleFr: "Accès via VPN sécurisé",
    titleEn: "Secure VPN access",
    bodyFr:
      "Connectivité chiffrée pour rejoindre un cloud privé, un site on-premise, un mode hybride ou un hébergement partenaire — sans nouveau mode de déploiement.",
    bodyEn:
      "Encrypted connectivity to reach a private cloud, on-premise site, hybrid setup or partner hosting — not a separate deployment mode.",
    href: "/contact?type=info",
  },
] as const;

export const KNOWLEDGE_WHY_POINTS = [
  {
    id: "sovereignty",
    titleFr: "Souveraineté et conformité",
    titleEn: "Sovereignty and compliance",
    bodyFr: "Certaines organisations doivent garder données et traitements sous leur contrôle réseau.",
    bodyEn: "Some organisations must keep data and processing under their own network control.",
  },
  {
    id: "connectivity",
    titleFr: "Réalité de connectivité",
    titleEn: "Connectivity reality",
    bodyFr: "Sites distants, VPN et contraintes bande passante orientent le choix SaaS, privé ou hybride.",
    bodyEn: "Remote sites, VPN and bandwidth constraints guide SaaS, private or hybrid choices.",
  },
  {
    id: "pace",
    titleFr: "Rythme de démarrage",
    titleEn: "Time to value",
    bodyFr: "Le SaaS accélère l’essai et la mise en production ; d’autres modes s’inscrivent dans un projet d’infrastructure.",
    bodyEn: "SaaS accelerates trial and go-live; other modes fit an infrastructure project.",
  },
  {
    id: "migration",
    titleFr: "Migration progressive",
    titleEn: "Progressive migration",
    bodyFr:
      "L’intent Migration (architecture V1) permet d’envisager un passage progressif entre modes lorsque le runtime multi-déploiement sera activé.",
    bodyEn:
      "The Migration intent (V1 architecture) supports a progressive move between modes when multi-deployment runtime is enabled.",
  },
] as const;

export const KNOWLEDGE_COMPARE_ROWS = [
  {
    id: "ops",
    criterionFr: "Exploitation",
    criterionEn: "Operations",
    saasFr: "ITaaS",
    saasEn: "ITaaS",
    privateFr: "Partagée / dédiée",
    privateEn: "Shared / dedicated",
    onpremFr: "Client",
    onpremEn: "Customer",
    hybridFr: "Mixte",
    hybridEn: "Mixed",
    partnerFr: "Partenaire + ITaaS",
    partnerEn: "Partner + ITaaS",
  },
  {
    id: "time",
    criterionFr: "Démarrage",
    criterionEn: "Time to start",
    saasFr: "Rapide",
    saasEn: "Fast",
    privateFr: "Projet",
    privateEn: "Project",
    onpremFr: "Projet infra",
    onpremEn: "Infra project",
    hybridFr: "Phasé",
    hybridEn: "Phased",
    partnerFr: "Projet",
    partnerEn: "Project",
  },
  {
    id: "control",
    criterionFr: "Contrôle réseau",
    criterionEn: "Network control",
    saasFr: "Plateforme",
    saasEn: "Platform",
    privateFr: "Élevé",
    privateEn: "High",
    onpremFr: "Maximal",
    onpremEn: "Maximum",
    hybridFr: "Sélectif",
    hybridEn: "Selective",
    partnerFr: "Contractuel",
    partnerEn: "Contractual",
  },
  {
    id: "vpn",
    criterionFr: "VPN sécurisé",
    criterionEn: "Secure VPN",
    saasFr: "Optionnel",
    saasEn: "Optional",
    privateFr: "Courant",
    privateEn: "Common",
    onpremFr: "Courant",
    onpremEn: "Common",
    hybridFr: "Courant",
    hybridEn: "Common",
    partnerFr: "Selon contrat",
    partnerEn: "Per contract",
  },
  {
    id: "v1",
    criterionFr: "Exécution V1",
    criterionEn: "V1 execution",
    saasFr: "Actif",
    saasEn: "Active",
    privateFr: "Contrat prêt",
    privateEn: "Contract-ready",
    onpremFr: "Contrat prêt",
    onpremEn: "Contract-ready",
    hybridFr: "Contrat prêt",
    hybridEn: "Contract-ready",
    partnerFr: "Contrat prêt",
    partnerEn: "Contract-ready",
  },
] as const;

export const KNOWLEDGE_FAQ = [
  {
    id: "what-is-suite",
    category: "fonctionnement" as const,
    qFr: "Qu’est-ce que ITaaS Suite ?",
    qEn: "What is ITaaS Suite?",
    aFr: "ITaaS Suite est la plateforme logicielle métier d’ITaaS RDC : modules connectés (éducation, RH, santé, stocks, finance, industrie…) présentés et activés via cette Product Experience Platform.",
    aEn: "ITaaS Suite is ITaaS RDC’s business software platform: connected modules (education, HR, health, inventory, finance, industry…) presented and activated through this Product Experience Platform.",
  },
  {
    id: "how-compose",
    category: "fonctionnement" as const,
    qFr: "Comment composer ma solution ?",
    qEn: "How do I compose my solution?",
    aFr: "Utilisez le configurateur (/configure) pour qualifier modules, volumes et projection commerciale, ou contactez-nous pour une démo accompagnée.",
    aEn: "Use the configurator (/configure) to qualify modules, volumes and commercial projection, or contact us for a guided demo.",
  },
  {
    id: "which-mode",
    category: "deploiement" as const,
    qFr: "Quel mode de déploiement choisir ?",
    qEn: "Which deployment mode should I choose?",
    aFr: "En V1, l’exécution standard est le SaaS. Cloud privé, on-premise, hybride et partner hosted sont décrits ici et prêts au contrat ; ils seront activés selon votre projet.",
    aEn: "In V1, standard execution is SaaS. Private cloud, on-premise, hybrid and partner hosted are described here and contract-ready; they are enabled according to your project.",
  },
  {
    id: "migration",
    category: "deploiement" as const,
    qFr: "Pouvons-nous migrer d’un mode à un autre ?",
    qEn: "Can we migrate from one mode to another?",
    aFr: "Oui, dans le cadre d’un projet. L’architecture prévoit l’intent Migration ; le chemin exact dépend de votre mode de départ, des données et de la connectivité (y compris VPN).",
    aEn: "Yes, as a project. Architecture includes the Migration intent; the exact path depends on your starting mode, data and connectivity (including VPN).",
  },
  {
    id: "security-baseline",
    category: "securite" as const,
    qFr: "Comment est assurée la sécurité ?",
    qEn: "How is security handled?",
    aFr: "Authentification et droits via la plateforme IAM, canaux chiffrés, et options VPN pour les accès aux environnements privés ou on-premise. Les exigences avancées se traitent en qualification commerciale.",
    aEn: "Authentication and rights via the IAM platform, encrypted channels, and VPN options for private or on-premise environments. Advanced requirements are handled in commercial qualification.",
  },
  {
    id: "vpn",
    category: "connectivite" as const,
    qFr: "Le VPN est-il un mode de déploiement ?",
    qEn: "Is VPN a deployment mode?",
    aFr: "Non. Le VPN est un moyen d’accès sécurisé aux modes cloud privé, on-premise, hybride ou partner hosted. Les modes officiels restent ceux de l’architecture publique V1.",
    aEn: "No. VPN is a secure access path to private cloud, on-premise, hybrid or partner hosted modes. Official modes remain those of the V1 public architecture.",
  },
  {
    id: "offline",
    category: "connectivite" as const,
    qFr: "Que se passe-t-il en cas de faible connectivité ?",
    qEn: "What happens with poor connectivity?",
    aFr: "Le SaaS nécessite une connexion pour les opérations en ligne. Les modes privés / on-premise / hybrides peuvent mieux coller à des sites avec contraintes réseau, avec un design d’accès (dont VPN) adapté.",
    aEn: "SaaS needs connectivity for online operations. Private / on-premise / hybrid modes can better fit sites with network constraints, with a suitable access design (including VPN).",
  },
  {
    id: "data-location",
    category: "donnees" as const,
    qFr: "Où sont hébergées les données ?",
    qEn: "Where is data hosted?",
    aFr: "En SaaS, l’hébergement suit l’infrastructure opérée pour ITaaS Suite. En cloud privé, on-premise ou partner hosted, l’emplacement est défini avec vous dans le projet de déploiement.",
    aEn: "In SaaS, hosting follows the infrastructure operated for ITaaS Suite. In private cloud, on-premise or partner hosted, location is defined with you in the deployment project.",
  },
  {
    id: "backup",
    category: "donnees" as const,
    qFr: "Qui gère sauvegardes et restauration ?",
    qEn: "Who manages backups and restore?",
    aFr: "En SaaS, les sauvegardes font partie de l’exploitation plateforme. Dans les autres modes, les responsabilités sont précisées au contrat (client, partenaire et/ou ITaaS).",
    aEn: "In SaaS, backups are part of platform operations. In other modes, responsibilities are set in the contract (customer, partner and/or ITaaS).",
  },
  {
    id: "licensing",
    category: "licences" as const,
    qFr: "Comment fonctionne la licence ?",
    qEn: "How does licensing work?",
    aFr: "ITaaS Suite est commercial : licences et abonnements selon modules, éditions et volumes. Le configurateur et l’équipe commerciale établissent la projection adaptée.",
    aEn: "ITaaS Suite is commercial: licences and subscriptions by modules, editions and volumes. The configurator and sales team build the right projection.",
  },
  {
    id: "trial",
    category: "licences" as const,
    qFr: "Existe-t-il un essai ?",
    qEn: "Is there a trial?",
    aFr: "Oui. L’intent d’activation V1 actif est l’essai (TRIAL), notamment via Configure et les parcours d’essai produit lorsque le module le permet.",
    aEn: "Yes. The active V1 activation intent is trial (TRIAL), notably via Configure and product trial journeys when the module allows it.",
  },
  {
    id: "support-hours",
    category: "support" as const,
    qFr: "Quel support est inclus ?",
    qEn: "What support is included?",
    aFr: "Le support standard (SLA STANDARD en V1) couvre l’accompagnement d’exploitation et les échanges avec ITaaS RDC. Des niveaux Business / 24×7 / mission critique sont prévus au contrat pour V1.1+.",
    aEn: "Standard support (SLA STANDARD in V1) covers operational guidance and exchanges with ITaaS RDC. Business / 24×7 / mission-critical tiers are contract-ready for V1.1+.",
  },
  {
    id: "local-team",
    category: "support" as const,
    qFr: "Où se trouve l’équipe ?",
    qEn: "Where is the team based?",
    aFr: "ITaaS RDC édite et accompagne depuis Lubumbashi. Contact commercial sous 48 h ouvrées en règle générale.",
    aEn: "ITaaS RDC publishes and supports from Lubumbashi. Commercial contact typically within 48 business hours.",
  },
  {
    id: "scale",
    category: "evolutivite" as const,
    qFr: "La plateforme évolue-t-elle avec notre croissance ?",
    qEn: "Does the platform scale with our growth?",
    aFr: "Oui : modules additionnels, volumes facturables et, à terme, bascule de mode de déploiement. Composez d’abord le périmètre utile, puis enrichissez.",
    aEn: "Yes: additional modules, billable volumes and, over time, deployment-mode changes. Compose the useful scope first, then expand.",
  },
  {
    id: "product-faq",
    category: "fonctionnement" as const,
    qFr: "Où trouver les FAQ par produit (Edu, Hospitol…) ?",
    qEn: "Where are product FAQs (Edu, Hospitol…)?",
    aFr: "Les FAQ produit (Edu, Hospitol, ERP, RH, Inventory, AutoID, Security, MinePortal) sont planifiées en backlog V1.1. Cette page couvre la FAQ générale plateforme.",
    aEn: "Product FAQs (Edu, Hospitol, ERP, HR, Inventory, AutoID, Security, MinePortal) are planned in backlog V1.1. This page covers the general platform FAQ.",
  },
] as const;

export const KNOWLEDGE_TRUST_PILLS = [
  { id: "security", labelFr: "Sécurité", labelEn: "Security" },
  { id: "ha", labelFr: "Haute disponibilité", labelEn: "High availability" },
  { id: "scale", labelFr: "Évolutivité", labelEn: "Scalability" },
  { id: "support", labelFr: "Support", labelEn: "Support" },
] as const;

export function knowledgeCopy(locale: PepLocale) {
  const en = locale === "en";
  return {
    seoTitle: en ? "Knowledge Center | ITaaS Suite" : "Centre de connaissances | ITaaS Suite",
    seoDescription: en
      ? "Deployment models, hybrid options, secure VPN access and general FAQ for ITaaS Suite — ITaaS RDC."
      : "Modèles de déploiement, options hybrides, accès VPN sécurisé et FAQ générale ITaaS Suite — ITaaS RDC.",
    seoKeywords: en
      ? ["ITaaS deployment", "SaaS", "private cloud", "on-premise", "hybrid", "VPN", "FAQ"]
      : ["déploiement ITaaS", "SaaS", "cloud privé", "on-premise", "hybride", "VPN", "FAQ"],

    heroBadge: en ? "Knowledge Center" : "Centre de connaissances",
    heroH1: en ? "Understand ITaaS Suite" : "Comprendre ITaaS Suite",
    heroLead: en
      ? "Flexible deployment models. Clear answers to your questions."
      : "Des modèles de déploiement flexibles. Des réponses claires à vos questions.",
    heroPrimaryCta: en ? "Request a demo" : "Demander une démo",
    heroPrimaryHref: "/contact?type=demo",
    heroSecondaryCta: en ? "Talk to an expert" : "Contacter un expert",
    heroSecondaryHref: "/contact",

    tabDeploy: en ? "Deployment models" : "Modèles de déploiement",
    tabFaq: en ? "General FAQ" : "FAQ générale",
    tabCompare: en ? "Comparison" : "Comparatif",

    modelsBadge: en ? "Deployment" : "Déploiement",
    modelsH2: en ? "Our deployment models" : "Nos modèles de déploiement",
    modelsLead: en
      ? "Choose how ITaaS Suite is hosted and operated — from SaaS to private, on-premise, hybrid or partner-hosted environments."
      : "Choisissez comment ITaaS Suite est hébergée et opérée — du SaaS au cloud privé, on-premise, hybride ou hébergement partenaire.",
    modelsCta: en ? "Compose my solution" : "Composer ma solution",
    modelsCtaHref: "/configure",
    modelsLearnMore: en ? "Learn more →" : "En savoir plus →",
    modelsActiveBadge: en ? "Active in V1" : "Actif en V1",
    modelsContractBadge: en ? "Contract-ready" : "Contrat prêt",

    whyH2: en ? "Why several deployment modes?" : "Pourquoi plusieurs modes de déploiement ?",
    whyLead: en
      ? "Organisations in the DRC and Central Africa do not share the same network, compliance or hosting constraints. Official modes cover those realities without changing the product catalogue."
      : "Les organisations en RDC et en Afrique centrale n’ont pas les mêmes contraintes réseau, conformité ou hébergement. Les modes officiels couvrent ces réalités sans changer le catalogue produit.",

    compareH2: en ? "Model comparison" : "Comparatif des modèles",
    compareLead: en
      ? "High-level view of operations, control and V1 readiness. Detailed scoping happens with our commercial team."
      : "Vue d’ensemble exploitation, contrôle et maturité V1. Le cadrage détaillé se fait avec l’équipe commerciale.",
    compareCriterion: en ? "Criterion" : "Critère",
    compareSaas: "SaaS",
    comparePrivate: en ? "Private cloud" : "Cloud privé",
    compareOnPrem: "On-Premise",
    compareHybrid: en ? "Hybrid" : "Hybride",
    comparePartner: en ? "Partner hosted" : "Partner hosted",

    faqH2: en ? "Frequently asked questions" : "Questions fréquentes",
    faqLead: en
      ? "General platform FAQ. Product-specific FAQs (Edu, Hospitol, …) are listed in backlog V1.1."
      : "FAQ générale plateforme. Les FAQ produit (Edu, Hospitol, …) sont planifiées en backlog V1.1.",
    faqSearchPlaceholder: en ? "Search a question…" : "Rechercher une question…",
    faqEmpty: en ? "No question matches your filters." : "Aucune question ne correspond à vos filtres.",
    faqSupportTitle: en ? "Still need help?" : "Vous ne trouvez pas votre réponse ?",
    faqSupportCta: en ? "Contact support" : "Contacter le support",
    faqSupportHref: "/contact",

    ctaH2: en ? "Ready to choose your deployment path?" : "Prêt à choisir votre mode de déploiement ?",
    ctaLead: en
      ? "Start with SaaS for a fast trial, or talk to us about private cloud, on-premise, hybrid, partner hosting and secure VPN access."
      : "Démarrez en SaaS pour un essai rapide, ou parlons cloud privé, on-premise, hybride, hébergement partenaire et accès VPN sécurisé.",
    ctaPrimary: en ? "Configure my solution" : "Configurer ma solution",
    ctaPrimaryHref: "/configure",
    ctaSecondary: en ? "Contact sales" : "Contacter le commercial",
    ctaSecondaryHref: "/contact?type=devis",
  };
}
